import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate as useRouterNavigate } from 'react-router-dom';
import { Portal } from './types';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { FieldUsers } from './pages/FieldUsers';
import { SubscriptionManage } from './pages/SubscriptionManage';
import { SubscriptionVerification } from './pages/SubscriptionVerification';
import { Bookings } from './pages/Bookings';
import { RetailerAssignment } from './pages/RetailerAssignment';
import { VisitScheduler } from './pages/VisitScheduler';
import { FAModule } from './pages/FAModule';
import { Operations } from './pages/Operations';
import { WhatsAppMessage } from './pages/WhatsAppMessage';
import { ReportsAnalytics } from './pages/ReportsAnalytics';
import { Settings } from './pages/Settings';
import { RetailerDirectory } from './pages/RetailerDirectory';
import { RetailerGroups } from './pages/RetailerGroups';
import { ManufacturerCatalog } from './pages/ManufacturerCatalog';
import { DistributorAssignment } from './pages/DistributorAssignment';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { CustomRetailers } from './pages/CustomRetailers';
import { ToastContainer, ToastType } from './components/Toast';
import { TokenGate } from './components/TokenGate';
import { AuthStore } from './hooks/useAuth';
import { useMyStore } from './hooks/useMyStore';

interface AppShellProps {
  store: AuthStore;
}

const resolvePortal = (store: AuthStore): Portal =>
  store.portal === 'distributor' ? 'distributor' : 'manufacturer';

const AppShell: React.FC<AppShellProps> = ({ store }) => {
  const initialPortal = resolvePortal(store);
  const location = useLocation();
  const routerNavigate = useRouterNavigate();
  const defaultPage = `${initialPortal === 'manufacturer' ? 'mfr' : 'dist'}-dashboard`;

  // Derive the active page from the URL so a browser refresh keeps the FSR
  // on the same screen. Supports two URL shapes:
  //   1. `/mfr-bookings` — preferred; the path-segment IS the page key
  //   2. `/?page=subscription-verification` — legacy redirect target from
  //      the payment service (kept for back-compat — replaced with a path
  //      navigation on first paint via the useEffect below).
  const currentPage = (() => {
    const qsPage = new URLSearchParams(location.search).get('page');
    if (qsPage) return qsPage;
    const pathPage = location.pathname.replace(/^\/+/, '').replace(/\/+$/, '');
    return pathPage || defaultPage;
  })();

  // If we arrived via `?page=...` (payment redirect), normalise the URL to
  // `/<page>` so a subsequent refresh works the same as any other page.
  useEffect(() => {
    const qsPage = new URLSearchParams(location.search).get('page');
    if (qsPage) {
      routerNavigate(`/${qsPage}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [portal, setPortal] = useState<Portal>(initialPortal);
  const [userCount, setUserCount] = useState(5);
  const [toasts, setToasts] = useState<any[]>([]);
  const [pageTitle, setPageTitle] = useState('');
  const [pageSubtitle, setPageSubtitle] = useState('');

  // Load full store details (name, emailId, mobileNumber) post-auth
  const { store: myStore } = useMyStore();

  const addToast = (message: string, type: ToastType = 'success') => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  // Welcome toast — fires once right after login completes, NOT on plain
  // page refreshes. Login.tsx sets `show_welcome` in sessionStorage on a
  // successful login; here we consume + remove it the first time store
  // details are available. Refreshing the page later won't re-trigger it
  // because the flag is already gone.
  useEffect(() => {
    if (sessionStorage.getItem('show_welcome') !== 'true') return;
    if (!myStore?.storeName) return; // wait for the store hook to resolve
    sessionStorage.removeItem('show_welcome');
    addToast(
      `Welcome back, ${myStore.storeName} (${portal === 'manufacturer' ? 'Manufacturer' : 'Distributor'})`,
      'success'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myStore?.storeName]);

  const navigate = (page: string) => {
    // Push the page key into the URL so refresh + browser back/forward
    // work as expected. `currentPage` re-derives from useLocation above.
    routerNavigate(`/${page}`);
    setPageTitle('');
    setPageSubtitle('');
    window.scrollTo(0, 0);
  };

  // Webhook simulation for bookings
  // useEffect(() => {
  //   if (currentPage.includes('dashboard') || currentPage.includes('bookings')) {
  //     const interval = setInterval(() => {
  //       addToast('New B2B Webhook: Order BK-7824 status updated to Dispatched', 'info');
  //     }, 28000);
  //     return () => clearInterval(interval);
  //   }
  // }, [currentPage]);

  // Each page key maps to its element. Aliased keys (mfr-X / dist-X)
  // declare two routes sharing the same element. The Layout sits OUTSIDE
  // the Routes so the sidebar / topbar don't unmount on navigation.
  const dashboardEl = <Dashboard portal={portal} onNavigate={navigate} />;
  const operationsEl = <Operations portal={portal} onNavigate={navigate} />;
  const bookingsEl = <Bookings portal={portal} />;
  const usersEl = <FieldUsers portal={portal} />;
  const subscriptionEl = (
    <SubscriptionManage userCount={userCount} setUserCount={setUserCount} />
  );
  const retailerAssignmentEl = (
    <RetailerAssignment
      portal={portal}
      setTitle={setPageTitle}
      setSubtitle={setPageSubtitle}
      showToast={addToast}
    />
  );
  const catalogEl = (
    <ManufacturerCatalog
      portal={portal}
      showToast={addToast}
      setTitle={setPageTitle}
      setSubtitle={setPageSubtitle}
    />
  );
  const settingsEl = <Settings onNavigate={navigate} />;

  return (
    <Layout
      portal={portal}
      currentPage={currentPage}
      onNavigate={navigate}
      setupComplete={true}
      title={pageTitle}
      subtitle={pageSubtitle}
      store={myStore}
    >
      <Routes>
        {/* Root → default dashboard for the active portal. */}
        <Route path="/" element={<Navigate to={`/${defaultPage}`} replace />} />

        {/* Dashboard */}
        <Route path="/mfr-dashboard" element={dashboardEl} />
        <Route path="/dist-dashboard" element={dashboardEl} />

        {/* FA module */}
        <Route path="/mfr-fa-module" element={<FAModule />} />
        <Route path="/dist-fa-module" element={<FAModule />} />

        {/* Bookings */}
        <Route path="/mfr-bookings" element={bookingsEl} />
        <Route path="/dist-bookings" element={bookingsEl} />

        {/* Field users */}
        <Route path="/mfr-users" element={usersEl} />
        <Route path="/dist-users" element={usersEl} />

        {/* Subscription */}
        <Route path="/setup-manage" element={subscriptionEl} />
        <Route path="/mfr-subscription" element={subscriptionEl} />
        <Route path="/dist-subscription" element={subscriptionEl} />
        <Route
          path="/subscription-verification"
          element={
            <SubscriptionVerification
              onContinue={() => navigate(defaultPage)}
              onRetry={() =>
                navigate(`${portal === 'manufacturer' ? 'mfr' : 'dist'}-subscription`)
              }
            />
          }
        />

        {/* Operations + the FA-tracker / FSR-performance / activity sub-views
            (all currently render the same Operations page). */}
        <Route path="/mfr-operations" element={operationsEl} />
        <Route path="/dist-operations" element={operationsEl} />
        <Route path="/mfr-fa-tracker" element={operationsEl} />
        <Route path="/dist-fa-tracker" element={operationsEl} />
        <Route path="/mfr-fsr-performance" element={operationsEl} />
        <Route path="/dist-fsr-performance" element={operationsEl} />
        <Route path="/mfr-activity" element={operationsEl} />
        <Route path="/dist-activity" element={operationsEl} />

        {/* Assignments */}
        <Route
          path="/mfr-assign-distributors"
          element={
            <DistributorAssignment
              setTitle={setPageTitle}
              setSubtitle={setPageSubtitle}
              showToast={addToast}
            />
          }
        />
        <Route path="/mfr-assign-retailers" element={retailerAssignmentEl} />
        <Route path="/dist-assign-retailers" element={retailerAssignmentEl} />

        {/* Visit scheduler */}
        <Route path="/mfr-visit-scheduler" element={<VisitScheduler />} />
        <Route path="/dist-visit-scheduler" element={<VisitScheduler />} />

        {/* Catalog */}
        <Route path="/mfr-catalog" element={catalogEl} />
        <Route path="/dist-catalog" element={catalogEl} />

        {/* WhatsApp / Reports / Settings */}
        <Route path="/mfr-whatsapp" element={<WhatsAppMessage />} />
        <Route path="/dist-whatsapp" element={<WhatsAppMessage />} />
        <Route path="/mfr-reports" element={<ReportsAnalytics />} />
        <Route path="/dist-reports" element={<ReportsAnalytics />} />
        <Route path="/mfr-settings" element={settingsEl} />
        <Route path="/dist-settings" element={settingsEl} />

        {/* Custom-store specific */}
        <Route path="/custom-retailers" element={<CustomRetailers showToast={addToast} />} />
        <Route path="/dist-retailer-directory" element={<RetailerDirectory />} />
        <Route path="/dist-retailer-groups" element={<RetailerGroups />} />

        {/* Unknown route — placeholder with "Back to Dashboard" CTA. */}
        <Route
          path="*"
          element={<UnknownPage onBack={() => navigate(defaultPage)} />}
        />
      </Routes>
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </Layout>
  );
};

/**
 * Fallback element for unknown URLs — replaces the inline `default:` arm
 * of the old `renderPage()` switch. Reads the active path so it can
 * surface a readable "this page name" hint.
 */
const UnknownPage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const location = useLocation();
  const name = location.pathname.replace(/^\/+/, '').replace(/-/g, ' ').toUpperCase() || 'PAGE';
  return (
    <div className="flex flex-col items-center justify-center h-full text-center">
      <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center text-text3 mb-4">
        <span className="text-4xl">🚧</span>
      </div>
      <h2 className="text-xl font-black text-text mb-2">{name}</h2>
      <p className="text-text2 max-w-xs">
        This page is currently under development as part of the Field Assist operations module.
      </p>
      <button onClick={onBack} className="btn-primary mt-6">
        Back to Dashboard
      </button>
    </div>
  );
};

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="*"
        element={<TokenGate>{(store) => <AppShell store={store} />}</TokenGate>}
      />
    </Routes>
  );
}
